/**
 * FixDiffPanel — MA-FUNNEL-DIFF-PANEL-016
 *
 * Shows a structured diff of what auto-fix changed on the canonical graph.
 * Renders nodes added, nodes updated (with before/after), edges added/removed.
 * Clicking a change dispatches a 'select-builder-node' event to highlight on canvas.
 */

import React from 'react';
import {
  Plus, ArrowRight, Minus, RefreshCw, X,
  GitBranch, CircleDot,
} from 'lucide-react';

function highlightNode(nodeId) {
  if (!nodeId) return;
  window.dispatchEvent(
    new CustomEvent('select-builder-node', { detail: { nodeId } })
  );
}

function NodeBadge({ type, isTerminal, outcome }) {
  const parts = [];
  if (type) parts.push(type.toUpperCase());
  if (isTerminal) parts.push('TERMINAL');
  if (outcome) parts.push(outcome.toUpperCase());
  if (!parts.length) return null;
  return (
    <span className="ml-1.5 text-[9px] font-medium tracking-wider text-slate-500 uppercase">
      {parts.join(' · ')}
    </span>
  );
}

function ChangeValue({ before, after }) {
  return (
    <span className="text-[11px] tabular-nums">
      <span className="text-red-400/80 line-through">{String(before ?? 'null')}</span>
      <ArrowRight className="inline h-2.5 w-2.5 mx-1 text-slate-600" />
      <span className="text-emerald-400">{String(after ?? 'null')}</span>
    </span>
  );
}

export function FixDiffPanel({ fixResults, onClose }) {
  if (!fixResults) return null;

  // Collect all changes from all applied fixes
  const allNodesAdded = [];
  const allNodesUpdated = [];
  const allEdgesAdded = [];
  const allEdgesRemoved = [];

  const fixes = Array.isArray(fixResults) ? fixResults : (fixResults.applied_fixes || []);

  fixes.forEach((fix) => {
    if (!fix.applied || !fix.changes) return;
    const c = fix.changes;
    if (c.nodes_added) allNodesAdded.push(...c.nodes_added);
    if (c.nodes_updated) allNodesUpdated.push(...c.nodes_updated);
    if (c.edges_added) allEdgesAdded.push(...c.edges_added);
    if (c.edges_removed) allEdgesRemoved.push(...c.edges_removed);
  });

  const totalChanges = allNodesAdded.length + allNodesUpdated.length
    + allEdgesAdded.length + allEdgesRemoved.length;

  if (totalChanges === 0) return null;

  return (
    <div
      data-testid="fix-diff-panel"
      className="border border-slate-700/60 rounded-lg bg-slate-900/80 backdrop-blur-sm overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-slate-800/60 border-b border-slate-700/40">
        <div className="flex items-center gap-2">
          <GitBranch className="h-3.5 w-3.5 text-violet-400" />
          <h3 className="text-xs font-semibold text-slate-200 tracking-wide">Changes Applied</h3>
          <span className="text-[10px] text-slate-500 tabular-nums">{totalChanges} change{totalChanges !== 1 ? 's' : ''}</span>
        </div>
        {onClose && (
          <button
            data-testid="diff-panel-close"
            onClick={onClose}
            className="p-0.5 rounded hover:bg-slate-700/50 text-slate-500 hover:text-slate-300 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Summary line */}
      <div className="px-3 py-1.5 flex flex-wrap gap-3 text-[10px] text-slate-500 border-b border-slate-700/30">
        {allNodesAdded.length > 0 && (
          <span className="flex items-center gap-1 text-emerald-400/80">
            <Plus className="h-2.5 w-2.5" />
            {allNodesAdded.length} node{allNodesAdded.length !== 1 ? 's' : ''} added
          </span>
        )}
        {allNodesUpdated.length > 0 && (
          <span className="flex items-center gap-1 text-amber-400/80">
            <RefreshCw className="h-2.5 w-2.5" />
            {allNodesUpdated.length} node{allNodesUpdated.length !== 1 ? 's' : ''} updated
          </span>
        )}
        {allEdgesAdded.length > 0 && (
          <span className="flex items-center gap-1 text-sky-400/80">
            <Plus className="h-2.5 w-2.5" />
            {allEdgesAdded.length} edge{allEdgesAdded.length !== 1 ? 's' : ''} created
          </span>
        )}
        {allEdgesRemoved.length > 0 && (
          <span className="flex items-center gap-1 text-red-400/80">
            <Minus className="h-2.5 w-2.5" />
            {allEdgesRemoved.length} edge{allEdgesRemoved.length !== 1 ? 's' : ''} removed
          </span>
        )}
      </div>

      {/* Change details */}
      <div className="px-3 py-2 space-y-2.5 max-h-[240px] overflow-y-auto">
        {/* Nodes Added */}
        {allNodesAdded.length > 0 && (
          <div className="space-y-1">
            <h4 className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Added Nodes</h4>
            {allNodesAdded.map((node, i) => (
              <button
                key={node.node_id || i}
                data-testid={`diff-node-added-${i}`}
                onClick={() => highlightNode(node.node_id)}
                className="w-full flex items-center gap-2 px-2 py-1 rounded text-left hover:bg-emerald-500/10 transition-colors group"
              >
                <Plus className="h-3 w-3 text-emerald-400 shrink-0" />
                <span className="text-xs text-emerald-300 group-hover:text-emerald-200 truncate">
                  {node.label}
                </span>
                <NodeBadge type={node.type} isTerminal={node.is_terminal} outcome={node.outcome} />
              </button>
            ))}
          </div>
        )}

        {/* Nodes Updated */}
        {allNodesUpdated.length > 0 && (
          <div className="space-y-1">
            <h4 className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Updated Nodes</h4>
            {allNodesUpdated.map((node, i) => (
              <button
                key={node.node_id || i}
                data-testid={`diff-node-updated-${i}`}
                onClick={() => highlightNode(node.node_id)}
                className="w-full px-2 py-1 rounded text-left hover:bg-amber-500/10 transition-colors group"
              >
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-3 w-3 text-amber-400 shrink-0" />
                  <span className="text-xs text-amber-300 group-hover:text-amber-200 truncate">
                    {node.label}
                  </span>
                </div>
                {node.changes && Object.entries(node.changes).map(([field, val]) => (
                  <div key={field} className="ml-5 mt-0.5 flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-500">{field}:</span>
                    <ChangeValue before={val.before} after={val.after} />
                  </div>
                ))}
              </button>
            ))}
          </div>
        )}

        {/* Edges Added */}
        {allEdgesAdded.length > 0 && (
          <div className="space-y-1">
            <h4 className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Added Edges</h4>
            {allEdgesAdded.map((edge, i) => (
              <button
                key={edge.edge_id || i}
                data-testid={`diff-edge-added-${i}`}
                onClick={() => highlightNode(edge.from_id)}
                className="w-full flex items-center gap-2 px-2 py-1 rounded text-left hover:bg-sky-500/10 transition-colors group"
              >
                <Plus className="h-3 w-3 text-sky-400 shrink-0" />
                <span className="text-xs text-sky-300 group-hover:text-sky-200 truncate">
                  {edge.from}
                </span>
                <ArrowRight className="h-3 w-3 text-slate-600 shrink-0" />
                <span className="text-xs text-sky-300 group-hover:text-sky-200 truncate">
                  {edge.to}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Edges Removed */}
        {allEdgesRemoved.length > 0 && (
          <div className="space-y-1">
            <h4 className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Removed Edges</h4>
            {allEdgesRemoved.map((edge, i) => (
              <button
                key={edge.edge_id || i}
                data-testid={`diff-edge-removed-${i}`}
                onClick={() => highlightNode(edge.from_id)}
                className="w-full flex items-center gap-2 px-2 py-1 rounded text-left hover:bg-red-500/10 transition-colors group"
              >
                <Minus className="h-3 w-3 text-red-400 shrink-0" />
                <span className="text-xs text-red-300 group-hover:text-red-200 truncate line-through">
                  {edge.from}
                </span>
                <ArrowRight className="h-3 w-3 text-slate-600 shrink-0" />
                <span className="text-xs text-red-300 group-hover:text-red-200 truncate line-through">
                  {edge.to}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default FixDiffPanel;
